import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Button,
  Container,
  Heading,
  Section,
  Text,
} from '../components/atomic';
import { ContentPageLayout } from '../layouts';

/**
 * Pricing page component for EllaFin
 *
 * Simple pricing placeholder with contact CTA for early access period.
 * Uses MUI theme values for all colors to support theme switching.
 *
 * @returns {JSX.Element} Pricing page component
 */
function Pricing() {
  const theme = useTheme();

  return (
    <ContentPageLayout backgroundColor={theme.palette.background.section}>
      <Section
        sx={{
          py: 0,
          position: 'relative',
          minHeight: 'calc(100vh - 200px)',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {/* Hero Header */}
        <Box
          sx={{
            borderTop: `4px solid ${theme.palette.secondary.main}`,
            borderBottom: `4px solid ${theme.palette.secondary.main}`,
            backgroundColor: `${theme.palette.secondary.main}0D`,
            py: 6,
            mb: 6,
            width: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center' }}>
              <Heading
                variant="h1"
                sx={{
                  color: 'text.primary',
                  fontWeight: 600,
                  fontSize: { xs: '2rem', md: '2.75rem' },
                  fontFamily: '"Playfair Display", serif',
                  mb: 2,
                }}
              >
                Pricing
              </Heading>
              <Text
                sx={{
                  fontSize: { xs: '1.1rem', md: '1.25rem' },
                  color: theme.palette.grey[500],
                  maxWidth: '600px',
                  mx: 'auto',
                }}
              >
                Simple, transparent pricing aligned with the value we deliver.
              </Text>
            </Box>
          </Container>
        </Box>

        {/* Pricing Card */}
        <Container maxWidth="md" sx={{ pt: 24, pb: 8 }}>
          <Box
            sx={{
              backgroundColor: 'white',
              p: { xs: 4, md: 6 },
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e8e8e8',
              textAlign: 'center',
              position: 'relative',
            }}
          >
            <Box
              sx={{
                display: 'inline-block',
                backgroundColor: `${theme.palette.primary.main}1A`,
                color: theme.palette.primary.main,
                px: 3,
                py: 1,
                borderRadius: '20px',
                fontSize: '0.9rem',
                fontWeight: 600,
                mb: 4,
              }}
            >
              Early Access
            </Box>

            <Heading
              variant="h2"
              sx={{
                mb: 3,
                fontSize: { xs: '1.5rem', md: '2rem' },
                fontWeight: 600,
                color: theme.palette.primary.main,
              }}
            >
              Contact Us for Pricing
            </Heading>

            <Text
              sx={{
                fontSize: '1.125rem',
                lineHeight: 1.8,
                color: theme.palette.grey[500],
                mb: 4,
                maxWidth: '500px',
                mx: 'auto',
              }}
            >
              We are currently onboarding early access customers with
              personalized pricing based on your AWS spend and reporting needs.
            </Text>

            <Box sx={{ mb: 4 }}>
              <Text sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                Early access includes:
              </Text>
              <Box
                component="ul"
                sx={{ textAlign: 'left', maxWidth: '300px', mx: 'auto', pl: 3 }}
              >
                <Text
                  component="li"
                  sx={{ mb: 1, color: theme.palette.grey[500] }}
                >
                  Priority onboarding support
                </Text>
                <Text
                  component="li"
                  sx={{ mb: 1, color: theme.palette.grey[500] }}
                >
                  Direct access to the product team
                </Text>
                <Text
                  component="li"
                  sx={{ mb: 1, color: theme.palette.grey[500] }}
                >
                  Influence on the product roadmap
                </Text>
                <Text component="li" sx={{ color: theme.palette.grey[500] }}>
                  Preferred pricing locked in
                </Text>
              </Box>
            </Box>

            <Button
              variant="contained"
              size="large"
              href="/early-access"
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: 'white',
                px: 5,
                py: 1.5,
                fontSize: '1.1rem',
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              Contact Sales
            </Button>
          </Box>
        </Container>
      </Section>
    </ContentPageLayout>
  );
}

export default Pricing;
